import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { CreateRecipeInput, Ingredient, Instruction, Recipe } from 'Types/api';

export function sanitizeFilename(name: string): string {
  // Keep alphanumeric, CJK characters (Chinese, Japanese, Korean), replace others with underscore
  // Chinese: \u4e00-\u9fff
  // Japanese Hiragana: \u3040-\u309f, Katakana: \u30a0-\u30ff
  // Korean Hangul: \uac00-\ud7af
  return name.replace(/[^\w\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff\uac00-\ud7af]/gi, '_');
}

function formatIngredient(ing: Ingredient): string {
  const parts = [ing.amount, ing.unit, ing.name].filter(Boolean);
  return parts.join(' ');
}

function getInstructionText(instruction: string | Instruction): string {
  return typeof instruction === 'string' ? instruction : instruction.text;
}

export interface RecipeExportData {
  title: string;
  description: string;
  category: string;
  ingredients: Ingredient[];
  instructions: (string | Instruction)[];
  prepTime: number;
  cookTime: number;
  servings: number;
  imageUrl?: string;
}

export function prepareRecipeForExport(recipe: Recipe): RecipeExportData {
  return {
    title: recipe.title,
    description: recipe.description,
    category: recipe.category,
    ingredients: recipe.ingredients,
    instructions: recipe.instructions,
    prepTime: recipe.prepTime,
    cookTime: recipe.cookTime,
    servings: recipe.servings,
    imageUrl: recipe.imageUrl,
  };
}

export function downloadAsJson(recipe: Recipe): void {
  const exportData = prepareRecipeForExport(recipe);
  const json = JSON.stringify(exportData, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeFilename(recipe.title)}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function createRecipeHtml(recipe: Recipe): HTMLDivElement {
  const container = document.createElement('div');
  container.style.cssText = `
    width: 800px;
    padding: 40px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    background: white;
    color: #333;
    line-height: 1.6;
  `;

  const meta = [
    recipe.prepTime ? `Prep: ${recipe.prepTime} min` : null,
    recipe.cookTime ? `Cook: ${recipe.cookTime} min` : null,
    recipe.servings ? `Servings: ${recipe.servings}` : null,
  ]
    .filter(Boolean)
    .join('  |  ');

  const ingredientsList = recipe.ingredients
    .map((ing) => `<li>${formatIngredient(ing)}</li>`)
    .join('');

  const instructionsList = recipe.instructions
    .map((inst) => `<li>${getInstructionText(inst)}</li>`)
    .join('');

  container.innerHTML = `
    <h1 style="margin: 0 0 8px 0; font-size: 28px; color: #1a1a1a;">${recipe.title}</h1>
    ${
      recipe.category
        ? `<p style="margin: 0 0 8px 0; font-style: italic; color: #666;">${recipe.category}</p>`
        : ''
    }
    ${meta ? `<p style="margin: 0 0 16px 0; font-size: 14px; color: #888;">${meta}</p>` : ''}
    ${
      recipe.description
        ? `<p style="margin: 0 0 24px 0; color: #555;">${recipe.description}</p>`
        : ''
    }
    ${
      recipe.imageUrl
        ? `<img id="recipe-export-img" src="${recipe.imageUrl}" style="width: 100%; max-height: 400px; object-fit: cover; border-radius: 8px; margin-bottom: 24px;" />`
        : ''
    }
    <h2 style="margin: 24px 0 12px 0; font-size: 20px; color: #1a1a1a; border-bottom: 2px solid #eee; padding-bottom: 8px;">Ingredients</h2>
    <ul style="margin: 0 0 24px 0; padding-left: 24px;">${ingredientsList}</ul>
    <h2 style="margin: 24px 0 12px 0; font-size: 20px; color: #1a1a1a; border-bottom: 2px solid #eee; padding-bottom: 8px;">Instructions</h2>
    <ol style="margin: 0; padding-left: 24px;">${instructionsList}</ol>
  `;

  return container;
}

async function loadImageAsDataUrl(url: string): Promise<string | null> {
  // Add cache-busting parameter to bypass browser cache that may not have CORS headers
  const cacheBustUrl = url + (url.includes('?') ? '&' : '?') + '_cb=' + Date.now();

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } else {
          resolve(null);
        }
      } catch {
        // CORS error when trying to read canvas - return null
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = cacheBustUrl;
  });
}

async function waitForImageLoad(img: HTMLImageElement): Promise<boolean> {
  return new Promise((resolve) => {
    if (img.complete && img.naturalHeight !== 0) {
      resolve(true);
    } else {
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    }
  });
}

export async function downloadAsPdf(recipe: Recipe): Promise<void> {
  const container = createRecipeHtml(recipe);

  // Add to DOM temporarily (hidden)
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  document.body.appendChild(container);

  try {
    // Handle image loading
    const img = container.querySelector('img') as HTMLImageElement | null;

    if (img && recipe.imageUrl) {
      // Try to convert to data URL to avoid CORS issues
      const dataUrl = await loadImageAsDataUrl(recipe.imageUrl);
      if (dataUrl) {
        img.src = dataUrl;
        await waitForImageLoad(img);
      } else {
        // CORS failed - remove image to prevent tainted canvas
        img.remove();
      }
    }

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      imageTimeout: 15000,
    });

    const pdf = new jsPDF('p', 'mm', 'a4');

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pdfWidth - margin * 2;

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = contentWidth / imgWidth;
    const scaledHeight = imgHeight * ratio;

    // Handle multi-page PDFs
    let heightLeft = scaledHeight;
    let position = margin;
    let page = 0;

    while (heightLeft > 0) {
      if (page > 0) {
        pdf.addPage();
        position = margin;
      }

      const sourceY = (page * (pdfHeight - margin * 2)) / ratio;
      const sourceHeight = Math.min((pdfHeight - margin * 2) / ratio, imgHeight - sourceY);

      if (sourceHeight <= 0) break;

      // Create a temporary canvas for this page slice
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = imgWidth;
      pageCanvas.height = sourceHeight;
      const ctx = pageCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(canvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);
        const pageImgData = pageCanvas.toDataURL('image/png');
        pdf.addImage(pageImgData, 'PNG', margin, position, contentWidth, sourceHeight * ratio);
      }

      heightLeft -= pdfHeight - margin * 2;
      page++;
    }

    pdf.save(`${sanitizeFilename(recipe.title)}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}

// ==================== IMPORT FUNCTIONS ====================

export interface ImportValidationError {
  field: string;
  message: string;
}

export interface ImportResult {
  success: boolean;
  data?: CreateRecipeInput;
  errors?: ImportValidationError[];
}

function isValidIngredient(ing: unknown): ing is Ingredient {
  if (typeof ing !== 'object' || ing === null) return false;
  const obj = ing as Record<string, unknown>;
  return (
    typeof obj.name === 'string' && typeof obj.amount === 'string' && typeof obj.unit === 'string'
  );
}

function isValidInstruction(inst: unknown): inst is string | Instruction {
  if (typeof inst === 'string') return true;
  if (typeof inst !== 'object' || inst === null) return false;
  const obj = inst as Record<string, unknown>;
  return typeof obj.text === 'string';
}

export function validateRecipeImport(data: unknown): ImportResult {
  const errors: ImportValidationError[] = [];

  if (typeof data !== 'object' || data === null) {
    return { success: false, errors: [{ field: 'root', message: 'Invalid JSON structure' }] };
  }

  const obj = data as Record<string, unknown>;

  // Required string fields
  if (typeof obj.title !== 'string' || !obj.title.trim()) {
    errors.push({ field: 'title', message: 'Title is required' });
  }
  if (typeof obj.description !== 'string') {
    errors.push({ field: 'description', message: 'Description must be a string' });
  }
  if (typeof obj.category !== 'string' || !obj.category.trim()) {
    errors.push({ field: 'category', message: 'Category is required' });
  }

  // Required number fields
  if (typeof obj.prepTime !== 'number' || obj.prepTime < 0) {
    errors.push({ field: 'prepTime', message: 'Prep time must be a non-negative number' });
  }
  if (typeof obj.cookTime !== 'number' || obj.cookTime < 0) {
    errors.push({ field: 'cookTime', message: 'Cook time must be a non-negative number' });
  }
  if (typeof obj.servings !== 'number' || obj.servings < 1) {
    errors.push({ field: 'servings', message: 'Servings must be at least 1' });
  }

  // Ingredients array
  if (!Array.isArray(obj.ingredients)) {
    errors.push({ field: 'ingredients', message: 'Ingredients must be an array' });
  } else if (obj.ingredients.length === 0) {
    errors.push({ field: 'ingredients', message: 'At least one ingredient is required' });
  } else {
    const invalidIngredients = obj.ingredients.filter((ing) => !isValidIngredient(ing));
    if (invalidIngredients.length > 0) {
      errors.push({
        field: 'ingredients',
        message: 'Each ingredient must have name, amount, and unit fields',
      });
    }
  }

  // Instructions array
  if (!Array.isArray(obj.instructions)) {
    errors.push({ field: 'instructions', message: 'Instructions must be an array' });
  } else if (obj.instructions.length === 0) {
    errors.push({ field: 'instructions', message: 'At least one instruction is required' });
  } else {
    const invalidInstructions = obj.instructions.filter((inst) => !isValidInstruction(inst));
    if (invalidInstructions.length > 0) {
      errors.push({
        field: 'instructions',
        message: 'Each instruction must be a string or have a text field',
      });
    }
  }

  // Optional imageUrl
  if (obj.imageUrl !== undefined && typeof obj.imageUrl !== 'string') {
    errors.push({ field: 'imageUrl', message: 'Image URL must be a string' });
  }

  if (errors.length > 0) {
    return { success: false, errors };
  }

  // Build the validated recipe input
  const recipeInput: CreateRecipeInput = {
    title: (obj.title as string).trim(),
    description: (obj.description as string) || '',
    category: (obj.category as string).trim(),
    ingredients: obj.ingredients as Ingredient[],
    instructions: obj.instructions as (string | Instruction)[],
    prepTime: obj.prepTime as number,
    cookTime: obj.cookTime as number,
    servings: obj.servings as number,
    imageUrl: obj.imageUrl as string | undefined,
    status: 'draft', // Imported recipes start as drafts
  };

  return { success: true, data: recipeInput };
}

export function parseRecipeFile(file: File): Promise<ImportResult> {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const data = JSON.parse(content);
        resolve(validateRecipeImport(data));
      } catch {
        resolve({
          success: false,
          errors: [{ field: 'file', message: 'Invalid JSON file' }],
        });
      }
    };

    reader.onerror = () => {
      resolve({
        success: false,
        errors: [{ field: 'file', message: 'Failed to read file' }],
      });
    };

    reader.readAsText(file);
  });
}
